/**
 * Bluedot Web SDK Service Module
 *
 * Provides centralized access to Bluedot SDK functionality including:
 * - Initialization
 * - Wave sending
 * - Location management
 * - User persistence
 *
 * Handles environment detection (webview vs standalone) and provides
 * appropriate behavior for each context.
 */

import { bluedot } from "@bluedot-innovation/javascript-sdk";
import bluedotConfig from "../config/bluedot";

// Module state
let isInitialized = false;
let initializationError = null;
let locationEnabled = false;
let locationPermissionRequested = false;

/**
 * Initialize the Bluedot SDK
 * Should be called once on application startup
 *
 * @returns {Promise<void>}
 */
export const initialize = async () => {
  if (!bluedotConfig.enabled) {
    log("Bluedot is disabled via configuration");
    return;
  }

  if (isInitialized) {
    log("Bluedot already initialized");
    return;
  }

  try {
    log("Initializing Bluedot SDK...");

    // Initialize the SDK with project ID
    await bluedot.initialize(bluedotConfig.projectId);
    log("Bluedot SDK initialized successfully");

    // Set debug mode if enabled
    if (bluedotConfig.debugMode) {
      bluedot.config.setDebugModeEnabled(true);
      log("Bluedot debug mode enabled");
    }

    // Set user persistence if enabled
    // Note: This requires user consent in production
    if (bluedotConfig.mockMode === false) {
      try {
        bluedot.config.setUserPersistenceEnabled(true);
        log("Bluedot user persistence enabled");
      } catch (persistenceError) {
        log("User persistence not available:", persistenceError.message);
      }
    }

    isInitialized = true;
    initializationError = null;
  } catch (error) {
    // If the SDK was already initialized (e.g. by bluedotWebViewIntegration),
    // treat this as success rather than a fatal error.
    if (error.message && error.message.includes("already initialized")) {
      log("Bluedot SDK was already initialized externally, syncing state");
      isInitialized = true;
      initializationError = null;
      return;
    }

    log("Failed to initialize Bluedot:", error.message);
    initializationError = error;
    isInitialized = false;
    throw error;
  }
};

/**
 * Send a wave event to Bluedot
 *
 * @param {string} destinationId - The destination ID for the wave
 * @param {Object} data - Wave data to send
 * @returns {Promise<Object>} - Wave response
 */
export const sendWave = async (destinationId, data = {}) => {
  if (!bluedotConfig.enabled) {
    log("Bluedot is disabled, skipping wave");
    return null;
  }

  if (!isInitialized) {
    log("Bluedot not initialized, initializing now...");
    try {
      await initialize();
    } catch (initError) {
      log("Auto-initialization failed in sendWave:", initError.message);
      return { error: initError.message };
    }
  }

  if (!destinationId) {
    log("No destinationId provided, skipping wave (branch may not have Bluedot configured)");
    return null;
  }

  try {
    log(`Sending wave to ${destinationId}:`, data);

    const rawMetaData = {
      ...data,
      timestamp: data.timestamp || new Date().toISOString()
    };

    const customEventMetaData = Object.fromEntries(
      Object.entries(rawMetaData).filter(
        ([, v]) => v != null && String(v).trim() !== ""
      )
    );

    const response = await bluedot.wave.send(destinationId, customEventMetaData);
    log("Wave sent successfully:", response);
    return response;
  } catch (error) {
    // Extract meaningful error message from various error shapes
    let errorMessage = "Unknown error";
    if (typeof error === "string") {
      errorMessage = error;
    } else if (error && error.message) {
      errorMessage = error.message;
    }
    // Bluedot SDK may attach errorObject with details
    if (error && error.errorObject) {
      try {
        const details =
          typeof error.errorObject === "string"
            ? error.errorObject
            : JSON.stringify(error.errorObject);
        errorMessage = `${errorMessage} - ${details}`;
      } catch (_) {
        // ignore stringify errors
      }
    }

    log("Failed to send wave:", errorMessage, error);
    // Don't throw - wave failures should not break user flow
    return { error: errorMessage, rawError: error };
  }
};

/**
 * Enable location tracking
 * IMPORTANT: Location permission can only be requested ONCE in web environment
 *
 * @returns {Promise<boolean>} - Whether location was enabled successfully
 */
export const enableLocation = async () => {
  if (!bluedotConfig.enabled) {
    log("Bluedot is disabled, skipping location enable");
    return false;
  }

  if (!isInitialized) {
    log("Bluedot not initialized, initializing now...");
    await initialize();
  }

  if (locationEnabled) {
    log("Location already enabled");
    return true;
  }

  if (locationPermissionRequested) {
    log("Location permission already requested (can only request once)");
    return false;
  }

  try {
    log("Enabling Bluedot location...");
    await bluedot.setLocationEnabled(true);
    locationEnabled = true;
    locationPermissionRequested = true;
    log("Bluedot location enabled successfully");
    return true;
  } catch (error) {
    log("Failed to enable location:", error.message);
    locationPermissionRequested = true;
    return false;
  }
};

/**
 * Check if Bluedot is ready for location
 *
 * @returns {Promise<boolean>}
 */
export const isLocationReady = async () => {
  if (!bluedotConfig.enabled || !isInitialized) {
    return false;
  }

  if (locationEnabled) {
    return true;
  }

  if (locationPermissionRequested) {
    return false;
  }

  return true;
};

/**
 * Reset Bluedot state
 * Useful for testing or when user logs out
 */
export const reset = () => {
  log("Resetting Bluedot state...");
  try {
    bluedot.reset();
  } catch (error) {
    log("Error during reset:", error.message);
  }
  isInitialized = false;
  initializationError = null;
  locationEnabled = false;
  locationPermissionRequested = false;
};

/**
 * Get initialization status
 *
 * @returns {Object} - Status object
 */
export const getStatus = () => {
  return {
    enabled: bluedotConfig.enabled,
    initialized: isInitialized,
    error: initializationError,
    locationEnabled,
    locationPermissionRequested,
    debugMode: bluedotConfig.debugMode,
    mockMode: bluedotConfig.mockMode
  };
};

/**
 * Log debug information
 *
 * @param {...any} args - Arguments to log
 */
const log = (...args) => {
  if (bluedotConfig.debugMode) {
    console.log("[Bluedot]", ...args);
  }
};

// Default export with all methods
const bluedotService = {
  initialize,
  sendWave,
  enableLocation,
  isLocationReady,
  reset,
  getStatus
};

export default bluedotService;
